import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import { getTicketById } from "../../../store/features/getTicketById";
import { getTicketMessagesById } from "../../../store/features/getTicketMessagesById";
import { updateTicket } from "../../../store/features/updateTicket";
import { ListCoRoleName } from "../../../store/features/ListCoUserByRole";
import { useSocket } from "../../../SocketContext";
import TeamMemberSelect from "../TicketSectionComponents/TicketMainSection/TeamMemberSelect";
import ChangeRequesterPopup from "../TicketSectionComponents/TicketChatSection/ChangeRequesterPopup";
import AddRequesterPopup from "../TicketSectionComponents/TicketChatSection/AddRequesterPopup";
import ChangeGroupPopup from "../TicketSectionComponents/TicketChatSection/ChangeGroupPopup";
import RequestTicket from "../TicketSectionComponents/TicketChatSection/Reguester";
import GroupTicket from "../TicketSectionComponents/TicketChatSection/TicketGroup";
import TicketTagInput from "../TicketSectionComponents/CreateTicket/TicketTagInput";
import "../TicketSectionComponents/TicketChatSection/style.css";
import { TV2_COLUMN_HEADER_HEIGHT } from "../../../constants/ticketV2Constants";
import { IconDetailsSplit24, IconInbox16, IconInbox24, IconPlus16 } from "./TicketV2Icons";
import {
  mapApiGroupsToSidebarGroups,
  buildDefaultGroupsFromTicket,
  normalizeTagIdsFromRaw,
  tagLabelForId,
  sameTagIdOrder,
  STATUS_LABEL_BY_API,
  mapApiPriorityToSelect,
} from "../../../utils/ticketV2DetailsUtils";
import { ROOT_URL } from "../../../api/ApiConfig";
import TranscriptModal from "../TicketSectionComponents/TicketChatSection/TranscriptModal";
import ConversationsHistoryModal from "./ConversationsHistoryModal";
import { getTicketUserHistory } from "../../../store/features/getTicketUserHistory";
import {
  inter,
  tv2SuperVisorOverrides,
  sxMetaLabel,
  tv2EmptyPlaceholderSx,
  tv2LinkActionSx,
  tv2UserDataCardSx,
} from "./ticketDetails/styles";
import { TicketDetailsSection } from "./ticketDetails/TicketDetailsSection";
import {
  DetailsFormSkeleton,
  HistoryPreviewSkeleton,
  StatusSelect,
  PrioritySelect,
  Field,
  SourceCreatedLastBlock,
} from "./ticketDetails/TicketDetailsSelects";

const EMPTY_TAGS = [];

function TicketDetails({
  ticket,
  ticketDetailLoading,
  onToggleDetails,
  onTicketListInvalidate,
}) {
  const dispatch = useDispatch();
  const socket = useSocket();
  const userData = useSelector((s) => s.loginToken);
  const ticketMessages = useSelector((s) => s.ticketMessages?.messages || []);
  const ticketId = ticket?._id || ticket?.id;
  const rawTicket = ticket?.raw || null;

  const [openSections, setOpenSections] = useState({
    properties: true,
    user: true,
    history: true,
    transcript: true,
    apps: false,
  });
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [assignee, setAssignee] = useState("");
  /** Tag _id strings persisted through the ticket PATCH endpoint. */
  const [tagIds, setTagIds] = useState([]);
  const availableTags = useSelector((s) => s.tags?.list || EMPTY_TAGS);
  const skipTagPersistRef = useRef(false);
  const prevTagIdsForPersistRef = useRef([]);
  /** Latest intended tags — persist always reads this so rapid add/remove/add sends one final PATCH. */
  const tagIdsRef = useRef([]);
  /** Monotonic generation; only the latest scheduled persist may write + refetch. */
  const tagPersistGenRef = useRef(0);
  /** Chains tag writes so in-flight PATCHes never race and refetch with stale tags. */
  const tagPersistChainRef = useRef(Promise.resolve());
  /** >0 while a tag write is queued/running — blocks syncing tags from rawTicket mid-write. */
  const pendingTagWritesRef = useRef(0);
  const ticketIdRef = useRef(ticketId);
  const pendingStatusApiRef = useRef(null);
  const prevTicketKeyForStatusRef = useRef("");

  const rawTagsKey = useMemo(() => {
    const t = rawTicket?.tags;
    if (!Array.isArray(t)) return "";
    return JSON.stringify(
      t.map((x) => (typeof x === "string" ? x : x?._id || x?.name || ""))
    );
  }, [rawTicket?.tags]);

  const tagOptions = useMemo(() => {
    const byId = new Map();
    const ticketTags = Array.isArray(rawTicket?.tags) ? rawTicket.tags : EMPTY_TAGS;
    [...availableTags, ...ticketTags].forEach((tag) => {
      if (!tag || typeof tag !== "object" || !tag._id) return;
      const id = String(tag._id);
      byId.set(id, { ...byId.get(id), ...tag, _id: id });
    });
    return Array.from(byId.values());
  }, [availableTags, rawTagsKey]);

  const [customerName, setCustomerName] = useState(ticket?.requester?.name || "");
  const [customerEmail, setCustomerEmail] = useState(ticket?.requester?.email || "");
  const [ccEmails, setCcEmails] = useState(ticket?.ccEmails || []);
  const [groups, setGroups] = useState(() => buildDefaultGroupsFromTicket(ticket));

  const [showChangeRequesterPopup, setShowChangeRequesterPopup] = useState(false);
  const [showAddRequesterPopup, setShowAddRequesterPopup] = useState(false);
  const [showChangeGroupPopup, setShowChangeGroupPopup] = useState(false);
  const [assigneePicklist, setAssigneePicklist] = useState([]);
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [showChatHistoryModal, setShowChatHistoryModal] = useState(false);
  const [chatTranscriptMessages, setChatTranscriptMessages] = useState([]);

  const ticketUserHistory = useSelector((s) => s.ticketUserHistory);
  const chatHistoryPreview = ticketUserHistory?.rows || [];
  const chatHistoryTotal = ticketUserHistory?.summary?.total || 0;
  const chatHistoryLoading = Boolean(ticketUserHistory?.loading);
  const historyCustomerId =
    ticketUserHistory?.customer?.id ||
    rawTicket?.customerId ||
    rawTicket?.customer_id ||
    rawTicket?.customer?._id ||
    rawTicket?.customer?.id ||
    ticket?.requester?._id ||
    ticket?.requester?.id ||
    "";
  const historyCustomerName =
    ticketUserHistory?.customer?.name || customerName || "Customer";

  useEffect(() => {
    if (!ticketId) return undefined;
    dispatch(getTicketUserHistory({ ticketId, page: 1, limit: 5 }));
  }, [dispatch, ticketId]);

  const teamMembersForSelect = useMemo(() => {
    const members = (assigneePicklist || []).map((item) => ({
      _id: item._id,
      full_name: item.full_name,
      email: item.email || "",
      agent_image: item.agent_image,
    }));
    const a = ticket?.assignee;
    if (a && a._id && !members.some((m) => m._id === a._id)) {
      members.unshift({
        _id: a._id,
        full_name: a.full_name || a.name,
        email: a.email || "",
        agent_image: a.agent_image,
      });
    }
    return members;
  }, [assigneePicklist, ticket?.assignee]);

  const ticketGroupIdForAssignees = useMemo(() => {
    return (
      groups?.[0]?._id ||
      groups?.[0]?.id ||
      ticket?.groups?.[0]?._id ||
      ticket?.groups?.[0]?.id ||
      ticket?.group?._id ||
      ticket?.group?.id ||
      rawTicket?.groups?.[0]?._id ||
      rawTicket?.group?._id ||
      ""
    );
  }, [
    groups,
    ticket?.groups,
    ticket?.group,
    rawTicket?.groups,
    rawTicket?.group,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await dispatch(
          ListCoRoleName({
            name: "agent,admin,admin_with_billing",
            group_id: ticketGroupIdForAssignees || undefined,
          })
        );
        if (cancelled) return;
        if (res.payload?.status === 200 && Array.isArray(res.payload.data)) {
          setAssigneePicklist(res.payload.data);
        } else {
          setAssigneePicklist([]);
        }
      } catch {
        if (!cancelled) setAssigneePicklist([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, ticketGroupIdForAssignees]);

  useEffect(() => {
    setCustomerName(ticket?.requester?.name || "");
    setCustomerEmail(ticket?.requester?.email || "");
    setCcEmails(ticket?.ccEmails || []);
    setGroups(buildDefaultGroupsFromTicket(ticket));

    const ticketKey = ticket?._id || ticket?.id || "";
    if (prevTicketKeyForStatusRef.current !== ticketKey) {
      prevTicketKeyForStatusRef.current = ticketKey;
      pendingStatusApiRef.current = null;
    }

    const apiStatus = String(ticket?.status || "").toLowerCase();
    const labelFromApi = STATUS_LABEL_BY_API[apiStatus] || "";
    const pending = pendingStatusApiRef.current;
    if (pending != null && pending !== "") {
      if (apiStatus === pending) {
        pendingStatusApiRef.current = null;
        setStatus(labelFromApi);
      }
      /* else: keep optimistic `status` — props are still stale */
    } else {
      setStatus(labelFromApi);
    }

    setPriority(mapApiPriorityToSelect(ticket?.priority));
    const a = ticket?.assignee;
    const aid =
      a && typeof a === "object" && a._id ? a._id : typeof a === "string" ? a : "";
    setAssignee(aid || "");
  }, [ticket?.id, ticket?._id, ticket?.status, ticket?.priority, ticket?.assignee, ticket?.group, ticket?.groups, ticket?.requester]);

  useEffect(() => {
    const chatRoom = rawTicket?.chatRoom;
    if (!chatRoom) {
      setChatTranscriptMessages([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${ROOT_URL}/message/get-message/${chatRoom}`);
        const res = await response.json();
        if (cancelled) return;
        if (res.status === 200 && Array.isArray(res.data)) {
          setChatTranscriptMessages(res.data);
        } else if (res.success && Array.isArray(res.messages)) {
          setChatTranscriptMessages(res.messages);
        } else {
          setChatTranscriptMessages([]);
        }
      } catch {
        if (!cancelled) setChatTranscriptMessages([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rawTicket?.chatRoom]);

  const transcriptMessages = useMemo(() => {
    if (ticketMessages.length > 0) return ticketMessages;
    return chatTranscriptMessages;
  }, [ticketMessages, chatTranscriptMessages]);

  const transcriptTicket = useMemo(
    () => ({
      _id: ticketId,
      subject: rawTicket?.subject || ticket?.subject || "",
      status: rawTicket?.status || ticket?.status || "",
      priority: rawTicket?.priority || ticket?.priority || "",
      customerName,
      customerEmail,
    }),
    [
      ticketId,
      rawTicket?.subject,
      rawTicket?.status,
      rawTicket?.priority,
      ticket?.subject,
      ticket?.status,
      ticket?.priority,
      customerName,
      customerEmail,
    ]
  );

  useEffect(() => {
    ticketIdRef.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    tagIdsRef.current = tagIds;
  }, [tagIds]);

  // Invalidate in-flight tag writes when switching tickets.
  useEffect(() => {
    tagPersistGenRef.current += 1;
    pendingTagWritesRef.current = 0;
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId) {
      skipTagPersistRef.current = true;
      prevTagIdsForPersistRef.current = [];
      tagIdsRef.current = [];
      setTagIds([]);
      return;
    }
    // Stale getTicketById / list refresh mid-write would wipe optimistic tags (blink).
    if (pendingTagWritesRef.current > 0) return;
    const ids = normalizeTagIdsFromRaw(rawTicket?.tags, tagOptions);
    skipTagPersistRef.current = true;
    prevTagIdsForPersistRef.current = ids;
    tagIdsRef.current = ids;
    setTagIds(ids);
  }, [ticketId, rawTagsKey, tagOptions]);

  useEffect(() => {
    if (skipTagPersistRef.current) {
      skipTagPersistRef.current = false;
      return;
    }
    if (!ticketId) return;
    if (sameTagIdOrder(prevTagIdsForPersistRef.current, tagIds)) return;

    const gen = ++tagPersistGenRef.current;
    const scheduledTicketId = ticketId;
    const rollbackTo = [...prevTagIdsForPersistRef.current];
    pendingTagWritesRef.current += 1;

    tagPersistChainRef.current = tagPersistChainRef.current
      .catch(() => {})
      .then(async () => {
        // Superseded by a newer add/remove before this slot ran — skip API entirely.
        if (gen !== tagPersistGenRef.current) return;
        if (ticketIdRef.current !== scheduledTicketId) return;

        const snapshot = [...tagIdsRef.current];
        if (sameTagIdOrder(prevTagIdsForPersistRef.current, snapshot)) return;

        try {
          await dispatch(
            updateTicket({
              ticketId: scheduledTicketId,
              updateData: { tags: snapshot },
            })
          ).unwrap();
          if (gen !== tagPersistGenRef.current) return;
          if (ticketIdRef.current !== scheduledTicketId) return;
          prevTagIdsForPersistRef.current = snapshot;
          toast.success("Tags updated");
          await dispatch(
            getTicketById({ ticketId: scheduledTicketId, silent: true })
          );
          if (gen !== tagPersistGenRef.current) return;
          if (ticketIdRef.current !== scheduledTicketId) return;
          onTicketListInvalidate?.();
        } catch (err) {
          if (gen !== tagPersistGenRef.current) return;
          if (ticketIdRef.current !== scheduledTicketId) return;
          skipTagPersistRef.current = true;
          prevTagIdsForPersistRef.current = rollbackTo;
          tagIdsRef.current = rollbackTo;
          setTagIds(rollbackTo);
          const msg =
            err?.data?.message || err?.message || "Failed to update tags";
          toast.error(msg);
        }
      })
      .finally(() => {
        // After a ticket switch we zero pending; don't let stale finally underflow.
        if (ticketIdRef.current !== scheduledTicketId) return;
        pendingTagWritesRef.current = Math.max(
          0,
          pendingTagWritesRef.current - 1
        );
      });
  }, [tagIds, ticketId, dispatch, onTicketListInvalidate]);

  const handleAssignSuccess = useCallback(async () => {
    if (!ticketId) return;
    try {
      const res = await dispatch(
        getTicketById({ ticketId, silent: true })
      ).unwrap();
      const data = res?.data?.ticket ?? res?.data ?? res;
      if (data?.customerName != null) setCustomerName(data.customerName);
      if (data?.customerEmail != null) setCustomerEmail(data.customerEmail);
      if (Array.isArray(data?.ccEmails)) setCcEmails(data.ccEmails);
      if (data?.groups) setGroups(mapApiGroupsToSidebarGroups(data.groups));
      await dispatch(
        getTicketMessagesById({ ticketId, silent: true })
      );
    } catch {
      /* keep local state */
    }
  }, [dispatch, ticketId]);

  const actorName = userData?.response?.user?.full_name || "Agent";

  const handleStatusChange = useCallback(
    async (newLabel) => {
      if (!ticketId || !newLabel || newLabel === status) return;
      const oldApiStatus = String(rawTicket?.status || ticket?.status || "").toLowerCase() || "open";
      const prevLabel =
        (status && String(status).trim()) ||
        STATUS_LABEL_BY_API[oldApiStatus] ||
        "";
      const optimisticId = `tv2-status-${Date.now()}`;
      pendingStatusApiRef.current = newLabel.toLowerCase();
      setStatus(newLabel);
      dispatch({
        type: "getTicketMessagesById/optimisticAdd",
        payload: {
          type: "events",
          meta: {
            type: "statusChange",
            actor: actorName,
            value: { from: prevLabel || newLabel, to: newLabel },
          },
          created_at: new Date().toISOString(),
          _id: optimisticId,
          isAdmin: true,
          agent: userData?.response?.user,
        },
      });
      try {
        await dispatch(
          updateTicket({
            ticketId,
            updateData: { status: newLabel.toLowerCase() },
          })
        ).unwrap();
        toast.success("Status updated successfully");
        if (socket && newLabel) {
          socket.emit("ticket-status-changed", {
            ticketId,
            oldStatus: oldApiStatus,
            newStatus: newLabel.toLowerCase(),
            agentId: userData?.response?.user?._id,
            businessId: userData?.response?.user?.business_id,
            ticket: { ...(rawTicket || {}), status: newLabel.toLowerCase() },
          });
        }
        await dispatch(
          getTicketMessagesById({ ticketId, silent: true })
        );
        await dispatch(getTicketById({ ticketId, silent: true }));
        onTicketListInvalidate?.();
      } catch (error) {
        pendingStatusApiRef.current = null;
        setStatus(prevLabel);
        dispatch({ type: "getTicketMessagesById/optimisticRemove", payload: optimisticId });
        const msg =
          error?.data?.message || error?.message || "Failed to update status";
        toast.error(msg);
      }
    },
    [
      ticketId,
      status,
      rawTicket,
      ticket?.status,
      dispatch,
      socket,
      userData?.response?.user,
      actorName,
      onTicketListInvalidate,
    ]
  );

  const handlePriorityChange = useCallback(
    async (newKey) => {
      if (!ticketId || !newKey || newKey === priority) return;
      const prevKey =
        (priority && String(priority).trim()) ||
        mapApiPriorityToSelect(rawTicket?.priority || ticket?.priority) ||
        "";
      const optimisticId = `tv2-priority-${Date.now()}`;
      setPriority(newKey);
      dispatch({
        type: "getTicketMessagesById/optimisticAdd",
        payload: {
          type: "events",
          meta: {
            type: "priorityChange",
            actor: actorName,
            value: { from: prevKey || newKey, to: newKey },
          },
          created_at: new Date().toISOString(),
          _id: optimisticId,
          isAdmin: true,
          agent: userData?.response?.user,
        },
      });
      try {
        await dispatch(
          updateTicket({
            ticketId,
            updateData: { priority: newKey.toLowerCase() },
          })
        ).unwrap();
        toast.success("Priority updated successfully");
        await dispatch(
          getTicketMessagesById({ ticketId, silent: true })
        );
        await dispatch(getTicketById({ ticketId, silent: true }));
        onTicketListInvalidate?.();
      } catch (error) {
        setPriority(prevKey);
        dispatch({ type: "getTicketMessagesById/optimisticRemove", payload: optimisticId });
        const msg =
          error?.data?.message || error?.message || "Failed to update priority";
        toast.error(msg);
      }
    },
    [
      ticketId,
      priority,
      rawTicket,
      ticket,
      dispatch,
      actorName,
      userData?.response?.user,
      onTicketListInvalidate,
    ]
  );

  const handleAssigneeChange = useCallback(
    async (e) => {
      const newId = e?.target?.value;
      if (!ticketId || !newId || newId === assignee) return;
      const oldId = assignee;
      const oldMember = teamMembersForSelect.find((m) => m._id === oldId);
      const newMember = teamMembersForSelect.find((m) => m._id === newId);
      const fromName = oldMember?.full_name || "Unassigned";
      const toName = newMember?.full_name || "Agent";
      const optimisticId = `tv2-assignee-${Date.now()}`;
      setAssignee(newId);
      dispatch({
        type: "getTicketMessagesById/optimisticAdd",
        payload: {
          type: "events",
          meta: {
            type: "assigneeChange",
            actor: actorName,
            value: { fromName, toName },
          },
          created_at: new Date().toISOString(),
          _id: optimisticId,
          isAdmin: true,
          agent: userData?.response?.user,
        },
      });
      try {
        await dispatch(
          updateTicket({
            ticketId,
            updateData: { assignee: newId },
          })
        ).unwrap();
        toast.success("Assignee updated successfully");
        await dispatch(
          getTicketMessagesById({ ticketId, silent: true })
        );
        await dispatch(getTicketById({ ticketId, silent: true }));
        onTicketListInvalidate?.();
      } catch (error) {
        setAssignee(oldId);
        dispatch({ type: "getTicketMessagesById/optimisticRemove", payload: optimisticId });
        const msg =
          error?.data?.message || error?.message || "Failed to update assignee";
        toast.error(msg);
      }
    },
    [
      ticketId,
      assignee,
      teamMembersForSelect,
      dispatch,
      actorName,
      userData?.response?.user,
      onTicketListInvalidate,
    ]
  );

  const handleCCEmailsUpdate = useCallback((updated) => {
    setCcEmails(updated);
  }, []);

  const sideBarDataForRequester = useMemo(
    () => ({
      _id: ticketId,
      customerName,
      customerEmail,
      ccEmails,
    }),
    [ticketId, customerName, customerEmail, ccEmails]
  );

  const toggle = (key) =>
    setOpenSections((p) => ({ ...p, [key]: !p[key] }));

  const removeTag = useCallback((tagId) => {
    setTagIds((prev) => prev.filter((id) => String(id) !== String(tagId)));
  }, []);

  return (
    <>
      {showChangeRequesterPopup && (
        <ChangeRequesterPopup
          setOpen={setShowChangeRequesterPopup}
          ticketId={ticketId}
          currentName={customerName}
          currentEmail={customerEmail}
          onAssignSuccess={handleAssignSuccess}
        />
      )}
      {showAddRequesterPopup && (
        <AddRequesterPopup
          setOpen={setShowAddRequesterPopup}
          ticketId={ticketId}
          onAssignSuccess={handleAssignSuccess}
          requesterEmail={customerEmail}
          existingCCEmails={ccEmails}
          onCCEmailsUpdate={handleCCEmailsUpdate}
        />
      )}
      {showChangeGroupPopup && (
        <ChangeGroupPopup
          open={showChangeGroupPopup}
          onClose={() => setShowChangeGroupPopup(false)}
          onSuccess={handleAssignSuccess}
          ticketId={ticketId}
          currentGroups={groups}
        />
      )}

      <TranscriptModal
        isOpen={showTranscriptModal}
        onClose={() => setShowTranscriptModal(false)}
        ticket={transcriptTicket}
        messages={transcriptMessages}
        title="Transcript"
      />

      <ConversationsHistoryModal
        isOpen={showChatHistoryModal}
        onClose={() => setShowChatHistoryModal(false)}
        customerId={historyCustomerId}
        customerName={historyCustomerName}
      />

      <Box
        component="aside"
        sx={{
          width: 280,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: inter,
          boxSizing: "border-box",
          bgcolor: "#FFF",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            height: TV2_COLUMN_HEADER_HEIGHT,
            minHeight: TV2_COLUMN_HEADER_HEIGHT,
            boxSizing: "border-box",
            flexShrink: 0,
            px: 2,
            borderBottom: "1px solid #E5E9EC",
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#061522", fontFamily: inter }}>
            Details
          </Typography>
          <IconButton
            size="small"
            sx={{ p: 0.5, color: "#405261" }}
            aria-label="Hide details panel"
            onClick={onToggleDetails}
          >
            <IconDetailsSplit24 />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", px: 2, py: 2 }}>
          {ticketDetailLoading ? (
            <DetailsFormSkeleton />
          ) : (
            <>
              <TicketDetailsSection
                title="Ticket Properties"
                open={openSections.properties}
                onToggle={() => toggle("properties")}
              >
                <Field label="Status">
                  <StatusSelect
                    value={status}
                    onChange={handleStatusChange}
                    placeholder="Select Option"
                  />
                </Field>
                <Field label="Priority">
                  <PrioritySelect
                    value={priority}
                    onChange={handlePriorityChange}
                    placeholder="Select Option"
                  />
                </Field>
                <Field label="Assignee">
                  <TeamMemberSelect
                    value={assignee}
                    onChange={handleAssigneeChange}
                    teamMembers={teamMembersForSelect}
                    placeholder="Select Option"
                    dropdownWidth="248px"
                    wrapperStyle={{ width: "248px", minWidth: "248px", display: "block" }}
                    triggerStyles={{ height: "40px" }}
                    dropDownTop="calc(100% + 8px)"
                    assignToMeMemberId={userData?.response?.user?._id}
                    showMemberEmailInDropdown={false}
                  />
                </Field>

                <Stack gap={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: 248 }}>
                    <Typography sx={sxMetaLabel}>Tags</Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    alignContent="center"
                    gap={1}
                    sx={{
                      width: 248,
                      maxWidth: "100%",
                      minHeight: 40,
                      p: "6px 0px",
                      borderRadius: "8px",
                      boxSizing: "border-box",
                    }}
                  >
                    {tagIds.map((tid) => (
                      <Box
                        key={tid}
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "999px",
                          border: "1px solid #CBD5DF",
                          padding: "0px 16px",
                          height: "32px",
                          flexShrink: 0,
                          textTransform: "capitalize",
                          fontWeight: 500,
                          fontSize: "12px",
                          color: "#405261",
                          fontFamily: inter,
                          boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                        }}
                      >
                        {tagLabelForId(tid, tagOptions)}
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTag(tid);
                          }}
                          sx={{
                            ml: 1,
                            mb: "2px",
                            cursor: "pointer",
                            fontSize: 16,
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </Box>
                      </Box>
                    ))}
                    <TicketTagInput
                      tags={tagIds}
                      setTags={setTagIds}
                      showHeader={false}
                      returnIds
                      uiMode="addButtonOnly"
                      dropDownTop="calc(100% + 8px)"
                    />
                  </Stack>
                </Stack>

                <SourceCreatedLastBlock ticket={ticket} />
              </TicketDetailsSection>

              <TicketDetailsSection title="User Data" open={openSections.user} onToggle={() => toggle("user")}>
                <Stack gap={2} sx={{ width: 248, maxWidth: "100%" }}>
                  {ticket?.requester?.name || ticket?.requester?.email ? (
                    <Box sx={tv2SuperVisorOverrides}>
                      <RequestTicket
                        variant="ticketv2"
                        sideBarData={sideBarDataForRequester}
                        setChangeRequesterPopup={setShowChangeRequesterPopup}
                        setShowAddRequesterPopup={setShowAddRequesterPopup}
                        onCCEmailsUpdate={handleCCEmailsUpdate}
                      />
                    </Box>
                  ) : (
                    <Stack gap={1.25} sx={tv2UserDataCardSx}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography
                          sx={{
                            color: "#061522",
                            fontFamily: inter,
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: "18px",
                          }}
                        >
                          Requester
                        </Typography>
                        <Typography
                          component="span"
                          sx={tv2LinkActionSx}
                          onClick={() => setShowAddRequesterPopup(true)}
                        >
                          Add Requester
                        </Typography>
                      </Stack>
                      <Box sx={tv2EmptyPlaceholderSx}>No Requester</Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={1.25}
                        onClick={() => setShowAddRequesterPopup(true)}
                        sx={{ cursor: "pointer", mt: 0.25 }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "16px",
                            border: "1px solid #E5E9EC",
                            background: "#FFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                            flexShrink: 0,
                          }}
                        >
                          <IconPlus16 />
                        </Box>
                        <Typography sx={tv2LinkActionSx}>Add more people</Typography>
                      </Stack>
                    </Stack>
                  )}
                  <Box sx={tv2SuperVisorOverrides}>
                    <GroupTicket
                      compact
                      headerTitle="Groups"
                      groups={groups}
                      onChangeGroup={() => setShowChangeGroupPopup(true)}
                    />
                  </Box>
                </Stack>
              </TicketDetailsSection>

              <TicketDetailsSection title="History" open={openSections.history} onToggle={() => toggle("history")}>
                {chatHistoryLoading && chatHistoryPreview.length === 0 ? (
                  <HistoryPreviewSkeleton rows={5} />
                ) : chatHistoryPreview.length > 0 ? (
                  <Stack gap={1}>
                    {chatHistoryPreview.map((row) => (
                      <Stack
                        key={row.id}
                        direction="row"
                        alignItems="center"
                        gap={1}
                        onClick={() => setShowChatHistoryModal(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setShowChatHistoryModal(true);
                          }
                        }}
                        sx={{
                          cursor: "pointer",
                          borderRadius: "8px",
                          "&:hover": { bgcolor: "#F9FAFA" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <IconInbox16 />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#405261",
                            fontFamily: inter,
                            fontWeight: 400,
                            lineHeight: "18px",
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.subject}
                        </Typography>
                      </Stack>
                    ))}
                    <Typography
                      component="button"
                      type="button"
                      onClick={() => setShowChatHistoryModal(true)}
                      sx={{
                        mt: 0.5,
                        p: 0,
                        border: 0,
                        background: "none",
                        cursor: "pointer",
                        textAlign: "left",
                        fontFamily: inter,
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: "18px",
                        color: "#148DFD",
                        textDecoration: "underline",
                        textDecorationSkipInk: "none",
                        textUnderlineOffset: "4px",
                        "&:hover": { color: "#0B7AD9" },
                      }}
                    >
                      View All {chatHistoryTotal}
                    </Typography>
                  </Stack>
                ) : (
                  <Box sx={tv2EmptyPlaceholderSx}>No History</Box>
                )}
              </TicketDetailsSection>

              <TicketDetailsSection
                title="Transcript"
                open={openSections.transcript}
                onToggle={() => toggle("transcript")}
              >
                {transcriptTicket.subject ? (
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    gap={1}
                    onClick={() => setShowTranscriptModal(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowTranscriptModal(true);
                      }
                    }}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "8px",
                      "&:hover": { bgcolor: "#F9FAFA" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: "-1px",
                      }}
                    >
                      <IconInbox24 />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#405261",
                        fontFamily: inter,
                        fontWeight: 400,
                        lineHeight: "18px",
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {transcriptTicket.subject}
                    </Typography>
                  </Stack>
                ) : (
                  <Box sx={tv2EmptyPlaceholderSx}>No transcript available</Box>
                )}
              </TicketDetailsSection>

              <TicketDetailsSection title="Apps" open={openSections.apps} onToggle={() => toggle("apps")}>
                <Typography sx={{ fontSize: 12, color: "#8B97A2", fontFamily: inter }}>
                  No apps connected.
                </Typography>
              </TicketDetailsSection>
            </>
          )}
        </Box>
      </Box>
    </>
  );
}

export default TicketDetails;
